import { useState, useEffect } from "react"
import ProductCard from "../../elements/product_card"
import ProductList from "../../elements/product_list"
import DiscountBanner from "../../components/DiscountBanner"
import { Link } from "react-router-dom"
import { BASE_URL } from "../../apiConfig"
import { toast } from "sonner"
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthProvider";
import { useLanguage } from "../../context/LanguageProvider";

export default function ProductsPage() {
  const { token , role, roles } = useAuth();
  const { t } = useLanguage();
  const [products, setProducts] = useState([])
  const [Newproducts, setNewProducts] = useState([])
  const [pinnedproducts, setPinnedProducts] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [viewMode, setViewMode] = useState(role != 0 ? "list" :"grid")
  const [sortBy, setSortBy] = useState("newest")
  const [showSortDropdown, setShowSortDropdown] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(16)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category")
  const [searchTimeout, setSearchTimeout] = useState(null)

  const fetchProducts = async (page = 1, search = "", isLoadMore = false, sortBy ) => {
    setError(null)
    if (!isLoadMore) {
      setLoading(true)
    } else {
      setIsFetchingMore(true)
    }
    
    try {
      const response = await fetch(BASE_URL + "/api/getproducts", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          category: category ? category : "",
          page: page,
          search: search, 
          sort: sortBy, 
        }),
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error(`API Error: ${response.status} - ${errorText}`)
        throw new Error(`Failed to fetch products: ${response.statusText}`)
      }

      const data = await response.json()

      if (data && data.data && data.data.data) {
        if (isLoadMore) {
          setProducts(prev => [...prev, ...data.data.data])
        } else {
          setProducts(data.data.data)
        }
        setNewProducts(data.nouveau)
        setPinnedProducts(data.pinned_ids)
        setCurrentPage(data.data.current_page)
        setTotalPages(data.data.last_page)
        setTotalItems(data.data.total)
        setItemsPerPage(data.data.per_page)
      } else if (Array.isArray(data)) {
        setProducts(isLoadMore ? prev => [...prev, ...data] : data)
      } else if (data && Array.isArray(data.products)) {
        setProducts(isLoadMore ? prev => [...prev, ...data.products] : data.products)
      } else if (data && Array.isArray(data.data)) {
        setProducts(isLoadMore ? prev => [...prev, ...data.data] : data.data)
      } else {
        console.error("API response for products is not an array:", data)
        if (!isLoadMore) setProducts([])
        toast.error(t('products.unexpected_format'))
      }
    } catch (err) {
      setError(err.message)
      toast.error(err.message || t('products.loading_error'))
    } finally {
      setLoading(false)
      setIsFetchingMore(false)
    }
  }

  useEffect(() => {
    fetchProducts(1, searchTerm)
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + document.documentElement.scrollTop + 100 >= document.documentElement.offsetHeight) {
        if (!loading && !isFetchingMore && currentPage < totalPages) {
          fetchProducts(currentPage + 1, searchTerm, true, sortBy)
        }
      }
    }
    
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [loading, isFetchingMore, currentPage, totalPages, searchTerm, sortBy, category])

  // Handle search input with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchTerm(value)
    
    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout)
    }
    
    // Set new timeout for debouncing
    setSearchTimeout(
      setTimeout(() => {
        fetchProducts(1, value)
      }, 500) // 500ms delay
    )
  }

  // Handle sort change
  // const handleSortChange = (value) => {
  //   setSortBy(value)
  //   setShowSortDropdown(false)
  //   fetchProducts(1, searchTerm) // Refetch with new sort
  // }

  const handleSortChange = (value) => {
    setSortBy(value)
    setShowSortDropdown(false)
    fetchProducts(1, searchTerm, false, value)
  }


  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "text-white bg-teal-500"
      case "inactive":
        return "text-red-500 bg-red-100"
      case "out_of_stock":
        return "text-white bg-red-500"
      default:
        return "text-gray-500 bg-gray-100"
    }
  }

  // Skeleton Loading Components
const ProductCardSkeleton = () => (
  <div className="group bg-white rounded-3xl shadow-lg border border-gray-100 animate-pulse">
    {/* Image Skeleton */}
    <div className="relative overflow-hidden rounded-t-3xl">
      <div className="aspect-square bg-gray-200 rounded-t-3xl"></div>
      <div className="absolute top-3 right-3 flex gap-1">
        <div className="bg-gray-300 w-16 h-5 rounded-full"></div>
        <div className="bg-gray-300 w-12 h-5 rounded-full"></div>
      </div>
    </div>

    {/* Content Skeleton */}
    <div className="px-4 pt-2 pb-4 max-lg:pb-3">
      <div className="flex justify-between items-center mb-2">
        <div className="bg-gray-200 w-20 h-4 rounded"></div>
        <div className="flex gap-0.5">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-gray-200 w-4 h-4 rounded"></div>
          ))}
        </div>
      </div>

      <div className="bg-gray-200 w-3/4 h-5 rounded mb-2"></div>
      <div className="bg-gray-200 w-full h-4 rounded mb-4"></div>

      {/* Pricing Skeleton */}
      <div className="mb-4 max-lg:mb-1.5">
        <div className="bg-gray-200 w-20 h-6 rounded mb-1"></div>
        <div className="bg-gray-200 w-32 h-4 rounded"></div>
      </div>

      {/* Action Buttons Skeleton */}
      <div className="flex gap-2">
        <div className="flex-1 bg-gray-200 h-10 rounded-xl"></div>
        <div className="bg-gray-200 w-10 h-10 rounded-xl"></div>
      </div>
    </div>
  </div>
)

const ProductListSkeleton = ({ role }) => (
  <div className="transition-all duration-300 border-b border-gray-100 animate-pulse">
                <div className={`w-full min-w-0 grid ${role !== 0 ? 'grid-cols-11 max-xl:grid-cols-9' : 'grid-cols-9 max-xl:grid-cols-7'} gap-6 items-center py-1 px-6 max-xl:px-3`}>
      
      {/* Product Info */}
      <div className="col-span-4 max-xl:col-span-2 flex items-center gap-4">
        <div className="w-14 h-14 bg-gray-200 rounded-xl"></div>
        <div className="flex-1 space-y-2">
          <div className="bg-gray-200 w-32 h-4 rounded"></div>
          <div className="bg-gray-200 w-24 h-3 rounded"></div>
        </div>
      </div>
      
      {/* Status */}
      <div className="col-span-1 space-y-1">
        <div className="bg-gray-200 w-16 h-5 rounded-full"></div>
        <div className="bg-gray-200 w-12 h-4 rounded-full"></div>
      </div>
      
      {/* Rating */}
      <div className="col-span-1">
        <div className="flex gap-0.5">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-gray-200 w-4 h-4 rounded"></div>
          ))}
        </div>
      </div>

      {/* Stock & Prices */}
      {role !== 0 && (
        <>
          <div className="col-span-1">
            <div className="bg-gray-200 w-8 h-4 rounded mx-auto"></div>
          </div>
          <div className="col-span-1">
            <div className="bg-gray-200 w-16 h-4 rounded"></div>
          </div>
        </>
      )}
      
      <div className="col-span-1">
        <div className="bg-gray-200 w-16 h-4 rounded"></div>
      </div>
      <div className="col-span-1">
        <div className="bg-gray-200 w-16 h-4 rounded"></div>
      </div>
      
      {/* Actions */}
      <div className="col-span-1 flex justify-center gap-1">
        {[...Array(role !== 0 ? 4 : 2)].map((_, i) => (
          <div key={i} className="bg-gray-200 w-8 h-8 rounded-xl"></div>
        ))}
      </div>
    </div>
  </div>
)


  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-1.5 pt-6 pb-12">
        {/* Discount Products Banner */}
        <div className="px-2">
          <DiscountBanner />
        </div>

        <div className="flex mx-2.5 flex-col md:flex-row justify-between items-start md:items-center mb-5 max">
          <div>
            <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
              {t('products.title')}
            </h1>
            <p className="text-sm max-xl:text-xs text-gray-600 max-w-2xl">
              {t('products.subtitle')}
            </p>
          </div>
          <div className="mt-6 md:mt-0">
            {role === 1 || (roles?.add_product === 1 && role === 2) ? (
              <Link to="/products/create">
                <button className="cursor-pointer text-xs max-xl:py-3 bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white font-semibold max-xl:py-2 max-xl:px-4 py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105">
                  <span className="flex items-center">
                    <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t('products.add_product')}
                  </span>
                </button>
              </Link>
            ) : (
              ""
            )}
          </div>
        </div>

        {/* Search and Controls */}
        <div className="bg-white mx-2.5 rounded-3xl shadow-lg border border-gray-100 px-4 py-2 max-xl:p-4 mb-5">
          <div className="flex flex-col items-center xl:flex-row gap-6 max-xl:gap-2">
            {/* Search Bar */}
            <div className="relative flex-1 py-2 w-full">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder={t('products.search_placeholder')}
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full outline-none dark:bg-stone-900 dark:text-white text-sm pl-12 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)] transition-all duration-200"
              />
            </div>

            {/* Controls */}
            <div className="flex gap-4 max-xl:w-full">
              <div className="relative">
                <button
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  className="flex cursor-pointer dark:bg-stone-900 items-center gap-2 px-4 py-3 border-2 border-gray-200 rounded-xl hover:border-[var(--color-primary-500)] transition-all duration-200"
                >
                  <svg className="h-4 w-4 text-gray-500 dark:text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z"
                    />
                  </svg>
                  <span className="text-xs dark:text-stone-300">{t('common.sort_by')}</span>
                  <svg
                    className={`h-3 w-3 text-gray-500 transition-transform ${showSortDropdown ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showSortDropdown && (
                  <div className="absolute top-full mt-2 left-0 w-48 bg-white border-2 border-gray-200 rounded-2xl shadow-xl z-10">
                    <div className="py-2 text-xs ">
                      {[
                        { value: "name", label: t('common.alphabetical') },
                        { value: "price", label: t('products.price') },
                        { value: "newest", label: t('common.newest_first') },
                        { value: "oldest", label: t('common.oldest_first') },
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => handleSortChange(option.value)}
                          className={`w-full cursor-pointer text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                            sortBy === option.value ? "bg-[var(--color-primary-50)] text-[var(--color-primary-500)] font-medium" : "text-gray-700"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* View Toggle */}
              <div className="flex bg-gray-100 max-h-max rounded-xl ml-2 overflow-hidden">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-3 cursor-pointer max-h-max transition-all duration-200 ${
                    viewMode === "grid"
                      ? "bg-[var(--color-primary-500)] text-white"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:bg-stone-900 dark:hover:bg-stone-800"
                  }`}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-3 cursor-pointer max-h-max transition-all duration-200 ${
                    viewMode === "list"
                      ? "bg-[var(--color-primary-500)] text-white"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:bg-stone-900 dark:hover:bg-stone-800"
                  }`}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 10h16M4 14h16M4 18h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading && (
        <div className="">
          {/* Grid View Skeleton */}
          {viewMode === "grid" ? (
            <div className="grid mt-20 px-4 grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-lg:gap-1.5 max-lg:gap-y-5">
              {[...Array(8)].map((_, i) => (
                <ProductCardSkeleton key={i} />
              ))}
            </div>
          ) : (
            /* List View Skeleton */
            <div className="space-y-2 bg-white rounded-xl shadow-sm">
              <div className="overflow-x-scroll custom-scrollbar">
                {/* Table Header (hidden on small screens) */}
                <div className={`hidden md:grid w-full min-w-0 ${role !== 0 ? 'md:grid-cols-11 max-xl:grid-cols-9' : 'md:grid-cols-9 max-xl:grid-cols-7'} gap-4 py-3 px-6 max-xl:px-3 bg-gray-50 rounded-t-lg border-b border-gray-200 mb-2`}>
                  {[...Array(role !== 0 ? 11 : 9)].map((_, i) => (
                    <div key={i} className="bg-gray-200 h-4 rounded"></div>
                  ))}
                </div>
                {/* Table Rows */}
                {[...Array(5)].map((_, i) => (
                  <ProductListSkeleton key={i} role={role} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
        {error && <p className="text-center text-red-500">{error}</p>}

        {!loading && !error && (
          <>
            {/* Results Count */}
            <div className="mb-8 px-2.5 flex justify-between items-center">
              <span className="inline-flex items-center px-4 py-2 bg-[var(--color-primary-50)] text-[var(--color-primary-500)] rounded-full text-[10px] font-medium">
                {t('common.showing')} {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, totalItems)} {t('common.of')}{" "}
                {totalItems} {t('products.products_lowercase')}
              </span>
              {totalPages > 1 && (
                <span className="text-xs text-gray-500">
                  {t('common.page')} {currentPage} {t('common.of')} {totalPages}
                </span>
              )}
            </div>

            {products.length > 0 ? (
              <>
                {viewMode === "grid" ? (
                  <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 lg:px-4 gap-6 max-lg:gap-1.5 max-lg:gap-y-5">
                    {products.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        getStatusColor={getStatusColor}
                        fetchProducts={() => fetchProducts(1, searchTerm)}
                        newProducts={Newproducts}
                        pinnedproducts={pinnedproducts}
                      />
                    ))}
                  </div>
                ) : (
                
                  <div className="space-y-2 mx-2.5 bg-white rounded-xl shadow-sm">
                    <div className="overflow-x-auto custom-scrollbar">
                      {/* Wrapper to enable horizontal scroll on small screens */}
                      <div className="min-w-[800px]">
                        <div
                          className={`grid w-full ${role !== 0 ? "grid-cols-11" : "grid-cols-9"} gap-2 sm:gap-3 lg:gap-6 py-3 px-3 sm:px-4 lg:px-6 bg-gray-50 rounded-t-lg border-b border-gray-200 mb-2 font-semibold text-gray-700 text-[10px] sm:text-xs`}
                        >
                          <div className="col-span-4 md:col-span-3">{t('products.product')}</div>
                          <div className="col-span-1">{t('products.status')}</div>
                          <div className="col-span-1">{t('products.rating')}</div>
                          {role !== 0 && (
                            <>
                              <div className="col-span-1 text-center">{t('products.stock')}</div>
                              <div className="col-span-1">{t('products.purchase_price')}</div>
                            </>
                          )}
                          <div className="col-span-1">{t('products.seller_price')}</div>
                          <div className="col-span-1">{t('products.suggested_price')}</div>
                          <div className="col-span-1 text-center">{t('products.actions')}</div>
                        </div>
                        {products.map((product) => (
                          <ProductList
                            role={role}
                            key={product.id}
                            product={product}
                            getStatusColor={getStatusColor}
                            fetchProducts={() => fetchProducts(1, searchTerm)}
                            newProducts={Newproducts}
                            pinnedproducts={pinnedproducts}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {isFetchingMore && (
                  viewMode === "grid" ? (
                    <div className="grid mt-6 px-4 grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-lg:gap-1.5 max-lg:gap-y-5">
                      {[...Array(8)].map((_, i) => (
                        <ProductCardSkeleton key={i} />
                      ))}
                    </div>
                  ) : (
                    <div className="flex justify-center items-center py-6 mt-4">
                      <div className="flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-md border border-gray-100">
                        <svg className="animate-spin h-5 w-5 text-[var(--color-primary-500)]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm font-medium text-gray-600 animate-pulse">{t('products.fetching_more')}</span>
                      </div>
                    </div>
                  )
                )}
                
                {!isFetchingMore && currentPage >= totalPages && products.length > 0 && (
                  <div className="flex justify-center items-center py-6 mt-4">
                    <span className="text-sm font-medium text-gray-400">{t('products.all_loaded')}</span>
                  </div>
                )}
              </>
            ) : (
              /* Empty State */
              <div className="text-center py-10">
                <div className="w-28 h-28 mx-auto mb-8 bg-[var(--color-primary-50)] rounded-full flex items-center justify-center">
                  <svg className="h-12 w-12 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{t('products.no_products_found')}</h3>
                <p className="text-gray-600 text-sm mb-6">
                  {t('common.adjust_search')}
                </p>
                {role === 1 || (roles?.add_product === 1 && role === 2) ? (
                  <Link href="/products/create">
                    <button className="bg-[var(--color-primary-500)] cursor-pointer text-sm hover:bg-[var(--color-primary-400)] text-white font-semibold py-3 px-6 rounded-2xl transition-all duration-300">
                      {t('products.create_first')}
                    </button>
                  </Link>
                ) : (
                  ""
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
